/**
 * English copy.
 *
 * This file is the shape the other locales must match — `zh-CN.ts` is typed
 * against it, so a missing key is a build error rather than a blank space in
 * somebody's UI.
 *
 * Voice: plain, warm, and specific. Say what a thing is, not what it is called.
 * "Available to reimburse" beats "Reimbursable balance"; "Snap the receipt"
 * beats "Upload documentation".
 */
export const en = {
  brand: {
    name: 'Cairn',
    tagline: 'Every receipt, stacked and safe.',
    // A cairn is a stack of stones that marks a trail — built one at a time,
    // standing for years. That is exactly what an HSA receipt pile is.
    about:
      'A cairn is a stack of stones that marks a trail. Build it one receipt at a time, and years from now it still shows you the way.',
  },

  common: {
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    done: 'Done',
    delete: 'Delete',
    remove: 'Remove',
    edit: 'Edit',
    add: 'Add',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    retry: 'Try again',
    search: 'Search',
    filter: 'Filter',
    filters: 'Filters',
    clear: 'Clear',
    clearAll: 'Clear all',
    apply: 'Apply',
    all: 'All',
    none: 'None',
    optional: 'Optional',
    required: 'Required',
    loading: 'Loading…',
    copy: 'Copy',
    copied: 'Copied',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    today: 'Today',
    of: 'of',
    more: 'More',
    showAll: 'Show all',
    showLess: 'Show less',
    unknown: 'Unknown',
    uncategorized: 'Uncategorized',
    everyone: 'Everyone',
    somethingWentWrong: 'Something went wrong',
    tryAgainOrRefresh: 'Try again, or refresh the page.',
  },

  ledger: {
    singular: 'Ledger',
    plural: 'Ledgers',
    all: 'All ledgers',
    allHint: 'Everything you have access to',
    switch: 'Switch ledger',
    current: 'Viewing',
    create: 'New ledger',
    createTitle: 'Create a ledger',
    createBody:
      'A ledger is one HSA — its own receipts, its own balance, and its own list of who can see it.',
    name: 'Ledger name',
    namePlaceholder: 'Alex’s HSA',
    description: 'Description',
    descriptionPlaceholder: 'What this account is for',
    currency: 'Currency',
    opened: 'Account opened on',
    openedHelp:
      'Expenses dated before this cannot be claimed against this account. Leave blank if unknown.',
    settings: 'Ledger settings',
    archive: 'Archive ledger',
    archiveConfirmTitle: 'Archive this ledger?',
    archiveConfirmBody:
      'It disappears from everyone who can see it. Nothing is deleted, and an administrator can bring it back.',
    receiptCount: { one: '{count} receipt', other: '{count} receipts' },
    emptyTitle: 'No ledgers yet',
    emptyBody: 'Create a ledger to start filing receipts.',
    noAccessTitle: 'Nothing shared with you yet',
    noAccessBody:
      'No ledgers have been shared with this account yet. Ask whoever invited you to share one.',
  },

  access: {
    title: 'People with access',
    subtitle: 'Everyone who can open this ledger.',
    share: 'Share',
    shareTitle: 'Share this ledger',
    shareBody: 'Choose who can see it, and what they can do.',
    addPeople: 'Add people or groups',
    searchPlaceholder: 'Search people and groups',
    noMatches: 'Nobody matches that',
    nobodyLeft: 'Everyone already has access',
    level: 'Access',
    view: 'Can view',
    contribute: 'Can add receipts',
    manage: 'Full access',
    viewHelp: 'See receipts and totals.',
    contributeHelp: 'See everything, and add or edit receipts.',
    manageHelp: 'Everything, plus recording reimbursements and sharing the ledger.',
    remove: 'Remove access',
    removeConfirmTitle: 'Remove {name}?',
    removeConfirmBody: 'They lose access to this ledger immediately.',
    lastManager: 'A ledger must keep at least one person with full access.',
    groupMembers: { one: '{count} member', other: '{count} members' },
    viaGroup: 'via {group}',
    you: 'You',
    onlyYou: 'Only you',
    sharedWith: { one: 'Shared with {count} person', other: 'Shared with {count} people' },
  },

  groups: {
    title: 'Groups',
    subtitle: 'Grant several people the same access at once.',
    create: 'New group',
    name: 'Group name',
    namePlaceholder: 'Bookkeepers',
    description: 'What this group is for',
    members: 'Members',
    addMember: 'Add someone',
    memberCount: { one: '{count} member', other: '{count} members' },
    empty: 'No groups yet',
    emptyHelp: 'Useful when several people need the same access.',
    deleteConfirmTitle: 'Delete {name}?',
    deleteConfirmBody: 'Everyone in it loses any access this group granted.',
  },

  nav: {
    overview: 'Overview',
    receipts: 'Receipts',
    add: 'Add',
    insights: 'Insights',
    settings: 'Settings',
  },

  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    signingIn: 'Signing in…',
    email: 'Email',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    forgotPassword: 'Forgot your password?',
    welcomeBack: 'Welcome back',
    signInSubtitle: 'Sign in to continue.',
    invalidCredentials: 'Incorrect email or password.',
    accountDisabled: 'This account is disabled. Contact an administrator.',
    resetTitle: 'Reset your password',
    resetSubtitle: 'Choose a new password for your account.',
    resetSent: 'If that address has an account, a reset link has been sent.',
    resetNoEmail:
      'No mail server is configured, so reset links cannot be sent. An administrator can generate one.',
    resetInvalid: 'This reset link has expired or has already been used.',
    resetDone: 'Password changed. You can sign in with it now.',
    passwordChanged: 'Password changed. Your other devices have been signed out.',
    passwordTooShort: 'Use at least 10 characters.',
    passwordMismatch: 'Passwords do not match.',
    currentPasswordWrong: 'Current password is incorrect.',
    strength: {
      label: 'Strength',
      weak: 'Weak',
      fair: 'Fair',
      good: 'Good',
      strong: 'Strong',
    },
  },

  setup: {
    title: 'Set up Cairn',
    subtitle: 'Create the first account. It becomes the administrator.',
    workspaceName: 'Workspace name',
    workspacePlaceholder: 'Chen family, Acme Ltd, …',
    workspaceHint: 'Shown on invitations and exports.',
    firstLedger: 'First ledger',
    firstLedgerHint: 'One ledger per HSA account. More can be added later.',
    yourName: 'Your name',
    createAccount: 'Create my account',
    creating: 'Setting up…',
    languageQuestion: 'Which language should Cairn start in?',
    almostThere: 'Almost there',
    finish: 'Finish',
  },

  invite: {
    title: 'Join {workspace}',
    titleGeneric: 'You have been invited',
    subtitle: 'Choose a password to finish setting up your account.',
    yourName: 'Your name',
    accept: 'Create my account',
    invalid: 'This invitation has expired or has already been used.',
    invalidHelp: 'Request a new invitation link.',
    signInInstead: 'Already have an account? Sign in',
  },

  dashboard: {
    greetingMorning: 'Good morning, {name}',
    greetingAfternoon: 'Good afternoon, {name}',
    greetingEvening: 'Good evening, {name}',
    availableToReimburse: 'Available to reimburse',
    availableExplainer: 'Out-of-pocket medical costs you have not claimed back yet.',
    acrossReceipts: { one: 'Across {count} receipt', other: 'Across {count} receipts' },
    totalSpent: 'Total spent',
    alreadyClaimed: 'Already claimed',
    thisYear: 'This year',
    oldestClaimable: 'Oldest unclaimed',
    oldestClaimableHint: 'HSA reimbursements have no filing deadline.',
    connectPaperless: 'Connect your Paperless',
    connectPaperlessBody:
      'Cairn stores every document in your Paperless-ngx. Point it at yours and you can start attaching receipts.',
    connectPaperlessAction: 'Connect',
    needsAttention: 'Needs a look',
    missingDocuments: {
      one: '{count} receipt has no document attached',
      other: '{count} receipts have no document attached',
    },
    missingDocumentsHelp: 'A reimbursement without documentation is hard to substantiate.',
    needsReview: {
      one: '{count} imported receipt needs checking',
      other: '{count} imported receipts need checking',
    },
    strandedWarning: 'Spent before your HSA opened',
    strandedHelp:
      '{amount} was spent before {date}, when your HSA was opened, so it cannot be claimed from it.',
    spendingOverTime: 'Spending over time',
    cumulativeClaimable: 'Claimable, running total',
    byCategory: 'Where it goes',
    byPerson: 'Who it is for',
    recentReceipts: 'Recently added',
    viewAll: 'View all',
    emptyTitle: 'Nothing filed yet',
    emptyBody: 'Add a receipt to get started.',
    emptyAction: 'Add a receipt',
  },

  receipts: {
    title: 'Receipts',
    searchPlaceholder: 'Search provider, note, or person',
    resultCount: { one: '{count} receipt', other: '{count} receipts' },
    totalInView: 'Total in view',
    claimableInView: 'Claimable in view',
    emptyTitle: 'No receipts yet',
    emptyBody: 'Add your first receipt.',
    emptyFilteredTitle: 'Nothing matches',
    emptyFilteredBody: 'Try widening the dates or clearing a filter.',
    groupThisMonth: 'This month',
    groupLastMonth: 'Last month',
    sort: {
      label: 'Sort',
      date_desc: 'Newest first',
      date_asc: 'Oldest first',
      amount_desc: 'Largest first',
      amount_asc: 'Smallest first',
      added_desc: 'Recently added',
    },
    filter: {
      person: 'Person',
      category: 'Category',
      status: 'Status',
      dateRange: 'Dates',
      amount: 'Amount',
      payment: 'Paid with',
      documents: 'Documents',
      withDocuments: 'Has a document',
      withoutDocuments: 'Missing a document',
      from: 'From',
      to: 'To',
      min: 'Min',
      max: 'Max',
      anyTime: 'Any time',
      thisYear: 'This year',
      lastYear: 'Last year',
      last12Months: 'Last 12 months',
      activeCount: { one: '{count} filter', other: '{count} filters' },
    },
  },

  status: {
    open: 'To claim',
    partial: 'Part claimed',
    reimbursed: 'Claimed',
    ineligible: 'Not eligible',
    openHelp: 'Available to reimburse. There is no filing deadline.',
    partialHelp: '{claimed} of {total} claimed back so far.',
    reimbursedHelp: 'Claimed back on {date}.',
    ineligibleHelp: 'Marked as not an eligible medical expense.',
  },

  payment: {
    label: 'Paid with',
    out_of_pocket: 'Own money',
    hsa_card: 'HSA card',
    other: 'Something else',
    outOfPocketHelp: 'You can claim this back from the HSA later.',
    hsaCardHelp: 'Already paid straight from the HSA — nothing left to claim.',
    otherHelp: 'Paid another way; not counted as claimable.',
  },

  eligibility: {
    label: 'Eligibility',
    eligible: 'Eligible',
    unsure: 'Not sure yet',
    ineligible: 'Not eligible',
    unsureHelp: 'Kept out of your claimable total until you confirm it.',
  },

  documentKind: {
    label: 'Document type',
    receipt: 'Receipt',
    eob: 'Explanation of benefits',
    invoice: 'Invoice',
    prescription: 'Prescription',
    statement: 'Statement',
    tax_form: 'Tax form (1099-SA / 5498-SA)',
    other: 'Other',
  },

  form: {
    newReceipt: 'New receipt',
    editReceipt: 'Edit receipt',
    amount: 'Amount',
    amountPlaceholder: '0.00',
    person: 'Who was it for',
    personPlaceholder: 'Choose a person',
    category: 'Category',
    categoryPlaceholder: 'Choose a category',
    provider: 'Clinic or provider',
    providerPlaceholder: 'Northgate Family Dental',
    serviceDate: 'Date of service',
    paidDate: 'Date paid',
    description: 'What it was for',
    descriptionPlaceholder: 'Cleaning and x-rays',
    notes: 'Notes',
    notesPlaceholder: 'Anything worth remembering later',
    attachDocument: 'Attach the receipt',
    takePhoto: 'Take a photo',
    chooseFile: 'Choose a file',
    addAnother: 'Save and add another',
    saveReceipt: 'Save receipt',
    deleteReceipt: 'Delete receipt',
    deleteConfirmTitle: 'Delete this receipt?',
    deleteConfirmBody: 'It disappears from your totals. The file stays in Paperless.',
    deleteAlsoDocument: 'Also delete the file from Paperless',
    amountRequired: 'Enter an amount',
    dateRequired: 'Pick a date',
    unsavedTitle: 'Discard this receipt?',
    unsavedBody: 'Your changes have not been saved.',
    discard: 'Discard',
    keepEditing: 'Keep editing',
  },

  detail: {
    title: 'Receipt',
    markReimbursed: 'Mark as claimed',
    markReimbursedPartial: 'Claim part of it',
    undoReimbursed: 'Mark as unclaimed',
    reimburseTitle: 'Claim this back',
    reimburseBody: 'Record what you have reimbursed yourself from the HSA.',
    reimburseAmount: 'Amount claimed',
    reimburseFull: 'The whole {amount}',
    reimburseDate: 'Date claimed',
    reimburseNote: 'Reference',
    reimburseNotePlaceholder: 'Transfer reference, cheque number…',
    documents: 'Documents',
    addDocument: 'Add a document',
    documentProcessing: 'Filing…',
    documentProcessingHelp: 'Filing in Paperless. This usually takes under a minute.',
    documentFailed: 'Could not be read',
    documentFailedHelp: 'The file is still in Paperless. Attach it again.',
    noDocuments: 'No document attached',
    noDocumentsHelp: 'Attach the receipt so this claim can be backed up later.',
    viewerZoom: 'Zoom',
    viewerFailed: 'That file could not be loaded. Paperless may be unreachable.',
    viewerUnsupported: 'This file cannot be shown here. Download it to open it.',
    download: 'Download',
    openInPaperless: 'Open in Paperless',
    addedBy: 'Added by {name}',
    addedOn: 'Added {date}',
    history: 'History',
  },

  insights: {
    title: 'Insights',
    subtitle: 'Where the money has gone, and what is still owed back to you.',
    range: 'Period',
    allTime: 'All time',
    byYear: 'By year',
    byCategoryTitle: 'Spending by category',
    byPersonTitle: 'Spending by person',
    monthlyTitle: 'Month by month',
    claimedVsOpen: 'Claimed vs still open',
    claimed: 'Claimed',
    stillOpen: 'Still open',
    spent: 'Spent',
    noData: 'Nothing to chart yet',
    noDataHelp: 'Add a few receipts and the picture builds itself.',
    tableView: 'View as table',
    chartView: 'View as chart',
    exportCsv: 'Export spreadsheet',
    exportArchive: 'Export archive',
    exportArchiveHelp: 'A spreadsheet plus every document, ready for an accountant.',
    exporting: 'Preparing your download…',
  },

  settings: {
    title: 'Settings',
    account: 'Account',
    profile: 'Your profile',
    name: 'Name',
    language: 'Language',
    appearance: 'Appearance',
    theme: { system: 'Match device', light: 'Light', dark: 'Dark' },
    security: 'Security',
    changePassword: 'Change password',
    activeSessions: 'Signed-in devices',
    signOutOthers: 'Sign out other devices',
    signOutOthersDone: 'Other devices signed out.',
    thisDevice: 'This device',
    lastSeen: 'Last used {date}',
    workspace: 'Workspace',
    workspaceName: 'Name',
    currency: 'Default currency',
    currencyHelp: 'Used for new ledgers. Each ledger can set its own.',
    ledgers: 'Ledgers',
    ledgersHelp: 'Each HSA gets its own ledger, with its own list of who can see it.',
    people: 'People in this ledger',
    peopleHelp: 'Everyone you file receipts for — including those without an account.',
    addPerson: 'Add a person',
    removePerson: 'Remove {name}',
    removePersonTitle: 'Remove {name}?',
    removePersonBody:
      'They will no longer be offered when filing a receipt. Receipts already recorded for them are unchanged.',
    personRemoved: 'Removed',
    personName: 'Name',
    relationship: 'Relationship',
    relationships: {
      self: 'Me',
      spouse: 'Partner',
      child: 'Child',
      parent: 'Parent',
      dependent: 'Dependent',
      other: 'Other',
    },
    personReceipts: { one: '{count} receipt', other: '{count} receipts' },
    archivePerson: 'Archive',
    archivedPerson: 'Archived',
    categories: 'Categories',
    categoriesHelp: 'Used to group your spending. Rename or add your own.',
    users: 'Accounts',
    usersHelp: 'Who can sign in. What they can see is set per ledger.',
    invitePerson: 'Invite someone',
    connections: 'Connections',
    paperless: 'Paperless',
    email: 'Email',
    about: 'About',
    version: 'Version',
    documentation: 'Documentation',
    dangerZone: 'Careful now',
    publicUrl: 'Address people use',
    publicUrlHelp: 'Used to build invitation links. Usually the address in your browser bar.',
  },

  users: {
    title: 'Accounts',
    role: 'Role',
    admin: 'Admin',
    member: 'Member',
    adminHelp: 'Can create accounts, manage groups, and change instance settings.',
    memberHelp: 'An ordinary account. What they see is decided per ledger.',
    adminNotice:
      'Administrators do not automatically see any ledger. Access is always granted explicitly.',
    grantAccess: 'Give access to',
    grantAccessHelp: 'Optional — you can share ledgers with them later instead.',
    status: 'Status',
    active: 'Active',
    invited: 'Invited',
    disabled: 'Turned off',
    neverSignedIn: 'Never signed in',
    lastSignIn: 'Last signed in {date}',
    inviteTitle: 'Invite someone',
    inviteBody: 'They pick their own password. Nothing to set up on your side.',
    inviteEmail: 'Their email',
    inviteName: 'Their name',
    sendEmail: 'Send them an email',
    sendEmailUnavailable: 'No mail server is set up, so you will get a link to share instead.',
    inviteSent: 'Invitation sent to {email}.',
    inviteLinkReady: 'Invitation ready — send them this link.',
    inviteLinkHelp: 'Anyone with this link can join, so share it directly.',
    pendingInvites: 'Waiting to join',
    expires: 'Expires {date}',
    resend: 'New link',
    revoke: 'Cancel invitation',
    resetPassword: 'Reset password',
    resetLinkReady: 'Send them this link to choose a new password.',
    disableUser: 'Turn off access',
    enableUser: 'Turn access back on',
    removeUser: 'Remove',
    removeConfirmTitle: 'Remove {name}?',
    removeConfirmBody: 'They lose access immediately. Receipts they added stay put.',
    lastAdmin: 'Someone has to stay an admin. Promote another person first.',
    emailTaken: 'That address already has an account.',
  },

  paperless: {
    title: 'Paperless',
    subtitle:
      'Cairn stores every document in your Paperless-ngx and reads what Paperless found. It does not run its own OCR, and it does not install Paperless.',
    notConnected: 'Not connected yet',
    notConnectedHelp: 'Point Cairn at your Paperless and you can start attaching documents.',
    connected: 'Connected',
    connectedTo: 'Connected to {url}',
    documentCount: {
      one: '{count} document in the archive',
      other: '{count} documents in the archive',
    },
    serverUrl: 'Paperless address',
    serverUrlPlaceholder: 'http://paperless:8000',
    username: 'Paperless username',
    passwordLabel: 'Paperless password',
    passwordHelp: 'Used once to fetch an API token. It is never stored.',
    useToken: 'Use an API token instead',
    usePassword: 'Use a username and password',
    apiToken: 'API token',
    connect: 'Connect',
    connecting: 'Connecting…',
    test: 'Test connection',
    testOk: 'Working — Paperless answered.',
    disconnect: 'Disconnect',
    unreachable: 'Cairn cannot reach that address. Check the URL and that Paperless is running.',
    unauthorized: 'Paperless turned down those details.',
    importTitle: 'Import what is already there',
    importBody:
      'Already have medical documents in Paperless? Bring them in as draft receipts, using whatever Paperless already recorded, and check them at your own pace.',
    importTag: 'Tag to import',
    importPreview: {
      one: '{count} document ready to import',
      other: '{count} documents ready to import',
    },
    importAction: 'Import',
    importDone: { one: 'Imported {count} receipt.', other: 'Imported {count} receipts.' },
    importNothing: 'Nothing new to import.',
  },

  sheets: {
    title: 'Google Sheets backup',
    subtitle:
      'Keep a copy of every receipt in a spreadsheet you own. If this server, its disk and its backups all go, the record survives — amounts, dates, who each visit was for, and a link to the document behind it.',
    off: 'Off',
    on: 'On',
    connected: 'Backing up',
    notConnected: 'Not set up',
    setUp: 'Set up',
    enable: 'Back up to Google Sheets',
    enableHelp: 'Optional. Nothing leaves this server until you turn it on.',
    spreadsheet: 'Spreadsheet',
    spreadsheetHelp: 'Paste the address of the spreadsheet, or just its ID.',
    spreadsheetPlaceholder: 'https://docs.google.com/spreadsheets/d/…',
    serviceAccount: 'Service account key',
    serviceAccountHelp: 'The JSON key file for a Google service account. Paste its contents here.',
    serviceAccountSaved: 'Key saved for {email}',
    shareReminder:
      'Share the spreadsheet with {email} and give it Editor access, or Google will refuse the write.',
    frequency: 'Back up',
    frequencyEntry: 'After every change',
    frequencyHourly: 'Hourly',
    frequencyDaily: 'Daily',
    frequencyWeekly: 'Weekly',
    frequencyManual: 'Only when I ask',
    tabsNote: 'Each ledger gets its own tab, rewritten in full so edits and deletions carry over.',
    test: 'Test connection',
    testOk: 'Connected to {title}.',
    syncNow: 'Back up now',
    syncing: 'Backing up…',
    syncOk: 'Backed up {rows} receipts across {ledgers} ledgers.',
    syncNothing: 'Nothing to back up yet.',
    lastSynced: 'Last backed up {when}',
    neverSynced: 'Not backed up yet',
    lastError: 'Last attempt failed: {message}',
    setupTitle: 'Set up the backup',
    setupSteps:
      'In the Google Cloud console: create a project, enable the Google Sheets API, create a service account, and download a JSON key. Then create a spreadsheet and share it with the service account as an Editor.',
  },

  emailSettings: {
    title: 'Email',
    subtitle: 'Optional. Without it, Cairn gives you links to share by hand.',
    host: 'SMTP server',
    port: 'Port',
    secure: 'Use TLS',
    username: 'Username',
    password: 'Password',
    from: 'Send from',
    test: 'Send a test',
    testOk: 'Your mail server answered.',
    testFailed: 'Could not reach that mail server.',
  },

  errors: {
    offline: 'You appear to be offline.',
    generic: 'Something went wrong.',
    notFound: 'We could not find that.',
    notFoundTitle: 'Receipt not found',
    couldNotLoad: 'Could not load',
    couldNotLoadBody:
      'The connection dropped, or the server was busy. Nothing has been lost — try again.',
    notFoundBody: 'It may have been deleted, or the link may be wrong.',
    goHome: 'Back to overview',
    sessionExpired: 'Your session has expired. Please sign in again.',
    fileTooLarge: 'That file is too big.',
    paperlessNotConfigured: 'Connect Paperless first to attach documents.',
    validation: 'Please check the highlighted fields.',
  },

  a11y: {
    menu: 'Menu',
    closeSheet: 'Close panel',
    chartDescription: 'Chart of {title}. The same figures are listed in the table below the chart.',
    documentPreview: 'Preview of {filename}',
    memberColor: 'Color marker for {name}',
  },
} as const;

export type Dictionary = typeof en;
